import i18next from "i18next";
import * as React from "react";
import { fromJSX, Link, Updater, Widget } from "widgets-for-react";
import { routeUpdaters } from "../../site/root/routes/routesState";
import { AuthButtonPrimary, FormControls, FormNotification, formSkeletonConfig, FormSkeletonLayout } from "../../utils/forms";
import { singleValidatorResultBuilders } from "../../validator/validator";
import { Api } from './api_to_state';
import { AuthState, isLoggedOutState, isResetPasswordState, ResetPasswordState } from "./authState";
import { getClassName, options } from "./utils";

function handleSubmit(e: React.FormEvent<HTMLFormElement>, setState: (_: Updater<AuthState>) => void, api: Api) {
  e.preventDefault();
  setState(s => {
    return isResetPasswordState(s)
      ? (api &&
        api.scorePassword &&
        api.scorePassword(s.login_data.password!).res == "strong" &&
        s.login_data.password == s.login_data.confirmed_password
        ? ({ ...s, resetPasswordState: "reset password - resetting password" })
        : ({
          ...s,
          login_data:
          {
            ...s.login_data,
            password: s.login_data.password == undefined
              ? ""
              : s.login_data.password, reset_password_token: s.login_data.reset_password_token == undefined
                ? ""
                : s.login_data.reset_password_token
          }
        })
      )
      : s
  })
}

const authTranslationWithFallback = (key: string, fallback: string) => { 
  const translation = i18next.t(`auth:${key}`)
  return translation === key ? fallback : translation
}

export function reset_password_form(
  options: options,
  s0: ResetPasswordState,
  readonly: boolean,
  api: Api,
  onContact: JSX.Element,
  displayNotification: boolean
): Widget<Updater<AuthState>> {
  const passwordScore = api.scorePassword(s0.login_data.password || '').res
  const passwordScoreError = (s0.login_data.password && api.scorePassword(s0.login_data.password).res !== "strong") || s0.resetPasswordState == 'reset password - error weak password'
  const confirmedPasswordError = s0.login_data.password != undefined && s0.login_data.confirmed_password != '' && s0.login_data.confirmed_password != s0.login_data.password;
  const passwordInputType = s0.login_data.show_password ? "text" : "password"
  
  return fromJSX((setState: (_: Updater<AuthState>) => void) => (
    <FormSkeletonLayout
      type="reset-password"
      kind="text-image"
      onSubmit={(e) => handleSubmit(e, setState, api)}
      options={options}
      image={formSkeletonConfig.imageLight}
    >
      <header className={getClassName(options, 'headerClassName', 'mb-4')}>
        <h1 className={getClassName(options, 'titleClassName', 'mb-4')}>
          {i18next.t('auth:resetFormHeader')}
        </h1>
        <p className={getClassName(options, 'introClassName', 'lead')}>
          {i18next.t('auth:resetFormPrompt')}
        </p>
      </header>

      {/* Notification */}
      {displayNotification && (
        <FormNotification
          options={options}
          message={
            s0.resetPasswordState === 'reset password - error weak password'
              ? i18next.t('auth:passwordTooWeakErrorNotification')
              : s0.resetPasswordState ===
                'reset password - error too many attempts'
                ? i18next.t('auth:resetErrorTooManyAttempts')
                : s0.resetPasswordState === "reset password - error token not valid"
                  ? <> 
                    {authTranslationWithFallback('resetErrorInvalidTokenPre', i18next.language === "en" ? "This link is not valid, please go to" : "Deze link is niet geldig, ga naar")}{' '}
                    <Link
                      to={routeUpdaters(i18next.language).requestResetPassword.url}
                      onClick={() => setState((s) =>
                        isResetPasswordState(s)
                        ? {
                            ...s,
                            resetPasswordRequestState: 'reset password request'
                          }
                        : s)}
                    >
                    {authTranslationWithFallback('resetErrorInvalidTokenPage', i18next.language === "en" ? "this page" : "deze pagina")}
                    </Link> 
                    {' '}{authTranslationWithFallback('resetErrorInvalidTokenPost', i18next.language === "en" ? "to reset your password." : "om een nieuw wachtwoord in te stellen.")}
                    </>
                  : i18next.t('auth:defaultErrorNotification')
          }
        />
      )}

      {/* Password */}
      <FormControls.FormItemInGroup
        controlId="password"
        label={i18next.t('auth:newPassword')}
        type={passwordInputType}
        readOnly={readonly}
        required={true}
        value={s0.login_data.password}
        passwordScore={passwordScore}
        onChange={(newValue) =>
          setState((s) =>
            isResetPasswordState(s)
              ? {
                ...s,
                resetPasswordRequestState: 'reset password',
                login_data: {
                  ...s.login_data,
                  password: newValue,
                },
              }
              : s
          )
        }
        validation={
          passwordScoreError
            ? singleValidatorResultBuilders.failed(
              'password',
              i18next.t('auth:weakPassword')
            )
            : singleValidatorResultBuilders.passed('password')
        }
        showPasswordToggle={{
          isChecked: s0.login_data.show_password,
          label: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePasswordShort'
              : 'auth:showPasswordShort'
          ),
          title: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePassword'
              : 'auth:showPassword'
          ),
          onChange: (newValue) =>
            setState((s) =>
              isResetPasswordState(s)
                ? {
                  ...s,
                  resetPasswordRequestState: 'reset password',
                  login_data: {
                    ...s.login_data,
                    show_password: newValue,
                  },
                }
                : s
            ),
        }}
      />

      {/* Confirm password */}
      <FormControls.FormItemInGroup
        controlId="confirmPassword"
        label={i18next.t('auth:confirmNewPassword')}
        type={passwordInputType}
        readOnly={readonly}
        required={true}
        value={s0.login_data.confirmed_password || ''}
        onChange={(newValue) =>
          setState((s) =>
            isResetPasswordState(s)
              ? {
                ...s,
                resetPasswordRequestState: 'reset password',
                login_data: {
                  ...s.login_data,
                  confirmed_password: newValue,
                },
              }
              : s
          )
        }
        validation={
          confirmedPasswordError
            ? singleValidatorResultBuilders.failed(
              'confirmPassword',
              i18next.t('auth:mismatchError')
            )
            : singleValidatorResultBuilders.passed('confirmPassword')
        }
        showPasswordToggle={{
          isChecked: s0.login_data.show_password,
          label: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePasswordShort'
              : 'auth:showPasswordShort'
          ),
          title: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePassword'
              : 'auth:showPassword'
          ),
          onChange: (newValue) =>
            setState((s) =>
              isResetPasswordState(s)
                ? {
                  ...s,
                  resetPasswordRequestState: 'reset password',
                  login_data: {
                    ...s.login_data,
                    show_password: newValue,
                  },
                }
                : s
            ),
        }}
      />

      {/* Submit */}
      <div className={getClassName(options, 'formActionsClassName', '')}>
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonPrimary
            kind="button"
            type="submit"
            disabled={readonly}
            label={
              readonly ? (
                <FormControls.SubmittingSpinner />
              ) : (
                i18next.t('auth:resetFormSubmit')
              )
            }
            onClick={() => null}
          />
        </div>
      </div>

      {/* Help */}
      <footer className={getClassName(options, 'footerClassName', 'mt-4')}>
        <p className={getClassName(options, 'footerItemClassName', 'mb-1')}>
          {i18next.t('auth:assistanceNeeded')}
          {/* {' '}{onContact}. */}
        </p>
      </footer>
    </FormSkeletonLayout>
  ))
}

export function reset_password_success(options: options): Widget<Updater<AuthState>> {
  return fromJSX((setState: (_: Updater<AuthState>) => void) => {
    return (
      <FormSkeletonLayout
        type="reset-password-success"
        kind="text-image"
        options={options}
        image={formSkeletonConfig.imageLight}
      >
        <header className={getClassName(options, "headerClassName", "mb-4")}>
          <h1 className={getClassName(options, "titleClassName", "mb-4")}>{i18next.t("auth:resetFormHeader")}</h1>
          <p className={getClassName(options, "introClassName", "lead")}>{i18next.t("auth:resetFormSuccess")}</p>
        </header>

        <div className={getClassName(options, "formActionsClassName", "")}>
          <div className={getClassName(options, "clickableWrapperClassName", "")}>
            <AuthButtonPrimary
              kind="link"
              to={routeUpdaters(i18next.language).login.url}
              label={i18next.t('auth:loginSubmit')}
              disabled={false}
              onClick={() => setState(s => 
                isLoggedOutState(s) ? ({...s, login_data:{...s.login_data, confirmed_password:"", password:"" }}) 
                : s)}
            />
          </div>
        </div>
      </FormSkeletonLayout>
    )
  })
}


